/**
 * Perps Exchange Registry.
 *
 * Two maps live here:
 * - `CEX_PERPS_EXCHANGES` — centralized perps (Bybit, Bitget, Kraken Futures, Crypto.com).
 * - `DEX_PERPS_EXCHANGES` — perps DEXes that ride this BE-proxied lane because their SDK class
 *    exposes the same surface (e.g. Katana). They don't share CEX-specific fields like
 *    `isOnlyCexPerps`; instead they may carry DEX-specific fields like `requiredChainId`.
 *
 * Consumers should prefer the union helpers (`isPerpsExchange`, `getPerpsExchangeConfig`,
 * `getAllPerpsExchanges`). The CEX-only helpers remain for code paths that need to gate on
 * "is this a real CEX" (e.g. the spot/perps duality flag).
 */
import { MarginMode } from "@cede/types";

interface BasePerpsExchangeConfig {
  id: string;
  name: string;
  logo: string;
  maxLeverage: number;
  defaultLeverage: number;
  minOrderValue: number;
  takerFee: number;
  makerFee: number;
  supportedFeatures: readonly ("market" | "limit" | "stopLoss" | "takeProfit")[];
  quoteAsset: string;
  /**
   * Token symbol used by the venue's balance/PnL settlement layer. Equal to `quoteAsset` for
   * venues where quote == settle (most CEX perps). Differs on DEXes that use a synthetic
   * settlement asset — e.g. Katana settles in `VBUSDC` even when the quote is `USD`.
   */
  settlementToken: string;
  /**
   * If set, the margin mode is locked to this value on this venue and the UI renders the
   * margin-mode selector as read-only. Used for venues that only support one margin mode
   * (e.g. Katana is cross-only).
   */
  lockedMarginMode?: MarginMode;
}

export interface CexPerpsExchangeConfig extends BasePerpsExchangeConfig {
  isOnlyCexPerps: boolean;
}

export interface DexPerpsExchangeConfig extends BasePerpsExchangeConfig {
  /** EVM chain id the user must sign on for the delegated-key onboarding (informational). */
  requiredChainId?: number;
}

export type PerpsExchangeConfig = CexPerpsExchangeConfig | DexPerpsExchangeConfig;

// TODO(CEX TP/SL): keep TP/SL disabled in the UI until API/SDK support is
// implemented and validated per exchange. Useful docs:
// - Bybit attached TP/SL: https://bybit-exchange.github.io/docs/v5/order/create-order
// - Bitget futures TP/SL: https://www.bitget.com/api-doc/contract/trade/Place-Order
// - Bitget position TP/SL: https://www.bitget.com/api-doc/classic/contract/plan/Place-Pos-Tpsl-Order
// - Kraken Futures conditional orders: https://docs.kraken.com/api/docs/futures-api/trading/send-order/
// - Crypto.com advanced orders: https://exchange-docs.crypto.com/exchange/v1/rest-ws/index.html
export const CEX_PERPS_EXCHANGES = {
  krakenfutures: {
    id: "krakenfutures",
    name: "Kraken Futures",
    logo: "kraken",
    maxLeverage: 50,
    defaultLeverage: 1,
    minOrderValue: 1,
    takerFee: 0.0005,
    makerFee: 0.0002,
    supportedFeatures: ["market", "limit"] as const,
    quoteAsset: "USD",
    settlementToken: "USD",
    isOnlyCexPerps: true,
  },
  bybit: {
    id: "bybit",
    name: "Bybit",
    logo: "bybit",
    maxLeverage: 100,
    defaultLeverage: 1,
    minOrderValue: 1,
    takerFee: 0.00055,
    makerFee: 0.0002,
    supportedFeatures: ["market", "limit"] as const,
    quoteAsset: "USDT",
    settlementToken: "USDT",
    isOnlyCexPerps: false,
  },
  // USDⓈ-M linear perps, keyed on the USDT-margined lane (deepest liquidity). maxLeverage
  // 125 (BTCUSDT ceiling) and minOrderValue 5 (MIN_NOTIONAL) are per-market fallbacks —
  // real values come from the SDK. Fees are undiscounted VIP0 (no BNB burn), like bybit.
  binance: {
    id: "binance",
    name: "Binance",
    logo: "binance",
    maxLeverage: 125,
    defaultLeverage: 1,
    minOrderValue: 5,
    takerFee: 0.0005,
    makerFee: 0.0002,
    supportedFeatures: ["market", "limit"] as const,
    quoteAsset: "USDT",
    settlementToken: "USDT",
    isOnlyCexPerps: false,
  },
  cryptocom: {
    id: "cryptocom",
    name: "Crypto.com",
    logo: "cryptocom",
    maxLeverage: 100,
    defaultLeverage: 1,
    minOrderValue: 1,
    takerFee: 0.0005,
    makerFee: 0.0002,
    supportedFeatures: ["market", "limit"] as const,
    quoteAsset: "USD",
    settlementToken: "USD",
    isOnlyCexPerps: false,
  },
  bitget: {
    id: "bitget",
    name: "Bitget",
    logo: "bitget",
    maxLeverage: 100,
    defaultLeverage: 1,
    minOrderValue: 1,
    takerFee: 0.0005,
    makerFee: 0.0002,
    supportedFeatures: ["market", "limit"] as const,
    quoteAsset: "USDT",
    settlementToken: "USDT",
    isOnlyCexPerps: false,
  },
  // USDT-margined perps (productGroup SwapU). maxLeverage / minOrderValue here are
  // fallbacks only — the real per-market values come from the SDK market data
  // (limits.leverage.max, limits.amount.min). takerFee 0.0006 is live-confirmed
  // (openFeeRate); makerFee 0.0002 is LBank's standard. LBank is perps-only in our
  // integration (no spot markets), so isOnlyCexPerps is true (like Kraken Futures).
  lbank: {
    id: "lbank",
    name: "LBank",
    logo: "lbank",
    maxLeverage: 125,
    defaultLeverage: 1,
    minOrderValue: 1,
    takerFee: 0.0006,
    makerFee: 0.0002,
    supportedFeatures: ["market", "limit"] as const,
    quoteAsset: "USDT",
    settlementToken: "USDT",
    isOnlyCexPerps: true,
  },
} as const satisfies Record<string, CexPerpsExchangeConfig>;

export type CexPerpsExchangeId = keyof typeof CEX_PERPS_EXCHANGES;

// Numbers are pending sdk-tester #5 verification. maxLeverage 20 is the digest §5
// reference value (per-market via imf_base); minOrderValue 10 is Katana's min_notional;
// makerFee 0 / takerFee 0.0005 are sdk-dev's defaults.
export const DEX_PERPS_EXCHANGES = {
  katana: {
    id: "katana",
    name: "Katana",
    logo: "katana",
    maxLeverage: 20,
    defaultLeverage: 1,
    minOrderValue: 10,
    takerFee: 0.0005,
    makerFee: 0,
    supportedFeatures: ["market", "limit"] as const,
    quoteAsset: "USD",
    settlementToken: "VBUSDC",
    lockedMarginMode: MarginMode.CROSS,
    requiredChainId: 747474,
  },
} as const satisfies Record<string, DexPerpsExchangeConfig>;

export type DexPerpsExchangeId = keyof typeof DEX_PERPS_EXCHANGES;
export type PerpsExchangeId = CexPerpsExchangeId | DexPerpsExchangeId;

export function isCexPerpsExchange(exchangeId: string): exchangeId is CexPerpsExchangeId {
  return exchangeId in CEX_PERPS_EXCHANGES;
}

export function isDexPerpsExchange(exchangeId: string): exchangeId is DexPerpsExchangeId {
  return exchangeId in DEX_PERPS_EXCHANGES;
}

export function isPerpsExchange(exchangeId: string): exchangeId is PerpsExchangeId {
  return isCexPerpsExchange(exchangeId) || isDexPerpsExchange(exchangeId);
}

export function isOnlyCexPerpsExchange(exchangeId: string): boolean {
  return CEX_PERPS_EXCHANGES[exchangeId]?.isOnlyCexPerps ?? false;
}

export function getCexPerpsExchangeConfig(exchangeId: string): CexPerpsExchangeConfig | undefined {
  if (isCexPerpsExchange(exchangeId)) {
    return CEX_PERPS_EXCHANGES[exchangeId];
  }
  return undefined;
}

export function getDexPerpsExchangeConfig(exchangeId: string): DexPerpsExchangeConfig | undefined {
  if (isDexPerpsExchange(exchangeId)) {
    return DEX_PERPS_EXCHANGES[exchangeId];
  }
  return undefined;
}

export function getPerpsExchangeConfig(exchangeId: string): PerpsExchangeConfig | undefined {
  return getCexPerpsExchangeConfig(exchangeId) ?? getDexPerpsExchangeConfig(exchangeId);
}

export function getAllCexPerpsExchanges(): CexPerpsExchangeConfig[] {
  return Object.values(CEX_PERPS_EXCHANGES);
}

export function getAllDexPerpsExchanges(): DexPerpsExchangeConfig[] {
  return Object.values(DEX_PERPS_EXCHANGES);
}

export function getAllPerpsExchanges(): PerpsExchangeConfig[] {
  return [...getAllCexPerpsExchanges(), ...getAllDexPerpsExchanges()];
}
